/**
 * The sign-in shell both portals share, built like the platform's own
 * sign-in pages: brand row, the form, a help line and the overlay scene.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./Icon";
import { useTheme } from "./Shell";
import "./auth.css";

export type AuthScene = { you: string; target: string; support: string };

export function AuthShell({ product, tag, help, scene, children }: { product: string; tag?: string; help: ReactNode; scene: AuthScene; children: ReactNode }) {
  useTheme();
  const panel = useRef<HTMLDivElement>(null);
  // The scene is a plain script that draws on the panel's canvas once it exists.
  useEffect(() => {
    if (!panel.current || panel.current.dataset.sceneRunning) return;
    const s = document.createElement("script");
    s.src = "/brand/overlay-scene.js";
    document.body.appendChild(s);
    return () => s.remove();
  }, []);
  return (
    <div className="auth">
      <div className="auth-pane" data-auth-pane>
        <header className="auth-brand">
          <img src="/brand/inlinea-mark.svg" alt="" />
          <b>Inlinea</b>
          <i aria-hidden />
          <span>
            {product}
            {tag && <span className="tag">{tag}</span>}
          </span>
        </header>
        <main className="auth-main">
          <div className="auth-content">{children}</div>
        </main>
        <footer className="auth-foot">
          <p>{help}</p>
          <p className="auth-secure">
            <Icon name="lock-closed" size={12} />
            Encrypted connection
          </p>
        </footer>
      </div>
      <aside className="auth-scene" aria-label="About Inlinea">
        <div className="auth-scene-panel" data-idp-scene data-scene-you={scene.you} data-scene-target={scene.target} ref={panel}>
          <canvas aria-hidden />
          <div className="auth-caption" data-idp-scene-caption>
            <p className="auth-eyebrow">Secure Access Platform</p>
            <h2 className="auth-headline">
              <span>Everything connected.</span> <span>Everything protected.</span>
            </h2>
            <p className="auth-support">{scene.support}</p>
          </div>
        </div>
      </aside>
    </div>
  );
}

export function AuthAlert({ tone = "red", children }: { tone?: "red" | "amber"; children: ReactNode }) {
  return (
    <div className={`banner ${tone}`} role={tone === "red" ? "alert" : undefined}>
      <Icon name={tone === "red" ? "error-circle" : "warning"} style={{ color: tone === "red" ? "var(--rd)" : "var(--am)" }} />
      <span>{children}</span>
    </div>
  );
}
